import { Array as Arr, Option, Schema } from 'effect';

import type { Page, PageRequest } from '@vs-shop/domain/pagination';
import { PriceCents } from '@vs-shop/domain/price';
import {
  type ProductSummary,
  toProductOfferCoupons,
} from '@vs-shop/engine/capability/productSchema';
import { toImageTemplate } from '@vs-shop/engine/image/ImageTemplate';
import type { HydraCollectionResponseType } from '@vs-shop/schemas/openapi/response';

import type { ProductBase } from '../schema/openapi/backend';

/**
 * rcprofi's product listing, curated.
 *
 * Both listing ports read `/api/v2/shop/products`: `CategoryListing`
 * filters it by taxon and `ProductCatalogue` does not. The body is the
 * same body, so the mapping is one module rather than a copy in each provider —
 * a difference between the two answers would be a defect and not a feature.
 */

/** One page of `/api/v2/shop/products`, as the generated schema decodes it. */
type ProductListingResponse = HydraCollectionResponseType<typeof ProductBase>;

/** One product as the listing returns it. */
type ListedProduct = ProductListingResponse['hydra:member'][number];

type ListedVariant = ListedProduct['variants'][number];

const decodeCents = Schema.decodeUnknownOption(PriceCents);

/**
 * A product as a listing returns it, curated.
 *
 * Money and stock live on rcprofi's variants, not on the product, so the first
 * variant is the offer a card shows. A product with no variant answers `None`
 * for every one of those fields, which is the honest reading of a product
 * nothing sells.
 *
 * `season` is `None` for every rcprofi product: the catalogue index carries no
 * season field. That is the curated shape's `Option` doing its job, not a gap
 * to fill — a field one backend lacks is absent, never invented.
 */
const toProductSummary = (product: ListedProduct): ProductSummary => {
  const variant = Arr.head(product.variants);
  const cents = (pick: (variant: ListedVariant) => number | undefined) =>
    Option.flatMap(variant, (only) => decodeCents(pick(only)));

  return {
    code: product.code,
    variantCode: Option.map(variant, (only) => only.code),
    defaultCartQuantity: Option.none(),
    name: Option.fromNullishOr(product.name),
    subtitle: Option.fromNullishOr(product.subtitle),
    season: Option.none(),
    href: Option.some(product.routerUrl),
    image: Option.flatMap(Option.fromNullishOr(product.mainImage), (image) =>
      toImageTemplate(image.pathAbstract)
    ),
    price: cents((only) => only.price),
    priceWithoutTax: cents((only) => only.priceWithoutTax),
    originalPrice: cents((only) => only.originalPrice),
    inStock: Option.flatMap(variant, (only) => Option.fromNullishOr(only.inStock)),
    maxOrderable: Option.flatMap(variant, (only) => Option.fromNullishOr(only.maxOrderable)),
    isFavourite: Option.flatMap(variant, (only) => Option.fromNullishOr(only.isFavourite)),
    listingFacts: {
      slug: product.slug,
      metaTitle: product.metaTitle,
      description: product.shortDescription,
      imagePath: product.mainImage?.pathAbstract ?? null,
      ageRestriction: product.ageRestriction,
      available: Option.getOrNull(
        Option.flatMap(variant, (only) => Option.fromNullishOr(only.available))
      ),
      stockStatus: Option.getOrNull(
        Option.flatMap(variant, (only) => Option.fromNullishOr(only.stockStatus))
      ),
      dispatchDate: Option.getOrNull(
        Option.flatMap(variant, (only) => Option.fromNullishOr(only.dispatchDate))
      ),
      localWarehouse: Option.getOrElse(
        Option.map(variant, (only) =>
          (only.localWarehouse ?? []).map((warehouse) => ({
            type: warehouse.type ?? null,
            quantity: warehouse.quantity,
            dispatchDate: warehouse.dispatchDate ?? null,
          }))
        ),
        () => []
      ),
      coupons: Option.getOrElse(
        Option.map(variant, (only) =>
          only.couponCodes.map((coupon) => ({
            code: coupon.code,
            promotedPrice: PriceCents.make(coupon.promotedPrice),
            style: coupon.style,
          }))
        ),
        () => []
      ),
      labels: Option.getOrElse(
        Option.map(variant, (only) =>
          only.labels.map((label) => ({
            code: label.code,
            color: label.color,
            textColor: label.textColor,
            icon: label.icon,
            name: label.name,
          }))
        ),
        () => []
      ),
    },
    coupons: Option.match(variant, {
      onNone: () => [],
      onSome: (only) => toProductOfferCoupons(only.couponCodes),
    }),
  };
};

/**
 * One page of the listing, curated.
 *
 * `page` and `itemsPerPage` come back from the request rather than from the
 * response: rcprofi answers a Hydra `hydra:view` of page links and not the
 * numbers themselves, and the page a caller asked for is the page it got.
 * `total` is the listing's own count, which is not the taxon's descendant
 * rollup — those are different numbers and the ports carry both, in different
 * places.
 */
export const toProductPage = (
  response: ProductListingResponse,
  asked: PageRequest
): Page<ProductSummary> => ({
  items: response['hydra:member'].map(toProductSummary),
  total: response['hydra:totalItems'],
  page: asked.page,
  itemsPerPage: asked.itemsPerPage,
  lastPage: response['hydra:view']?.hydraViewNumbers.last,
});
